import type { PropType } from 'virtual-vue';
import type { TabsIndex, TabsType, TabsBackground, TabsSize } from './tabs';
import type { TabItemIcons } from './tab-item';

export const DEFAULT_TABS_TYPE = 'normal';
export const DEFAULT_TABS_BACKGROUND = 'white';
export const DEFAULT_TABS_SIZE = 'medium';

/**
 * Tabs 组件的 props
 */
export const tabsProps = {
  /**
   * 当前选中标签的索引 (Vue 3)
   * @default 0
   */
  modelValue: {
    type: [String, Number] as PropType<TabsIndex>,
    default: 0,
  },
  /**
   * 当前选中标签的索引 (Vue 2)
   * @default 0
   */
  value: {
    type: [String, Number] as PropType<TabsIndex>,
    default: 0,
  },
  /**
   * 选项卡样式类型
   * @default 'normal'
   */
  type: {
    type: String as PropType<TabsType>,
    default: DEFAULT_TABS_TYPE,
  },
  /**
   * 选项卡背景色
   * @default 'white'
   */
  background: {
    type: String as PropType<TabsBackground>,
    default: DEFAULT_TABS_BACKGROUND,
  },
  /**
   * 选项卡尺寸
   * @default 'medium'
   */
  size: {
    type: String as PropType<TabsSize>,
    default: DEFAULT_TABS_SIZE,
  },
  /**
   * 是否显示渐变遮罩
   * @default true
   */
  showGradient: {
    type: Boolean,
    default: true,
  },
  /**
   * 自定义激活颜色
   */
  activeColor: {
    type: String,
    default: undefined,
  },
  /**
   * 自定义未激活颜色
   */
  inactiveColor: {
    type: String,
    default: undefined,
  },
  /**
   * 是否开启手势滑动切换
   * @default false
   */
  swipeable: {
    type: Boolean,
    default: false,
  },
  /**
   * 是否开启切换动画
   * @default true
   */
  animated: {
    type: Boolean,
    default: true,
  },
  /**
   * 动画持续时间(毫秒)
   * @default 300
   */
  duration: {
    type: Number,
    default: 300,
  },
  /**
   * 自定义底部横条的宽度（像素）
   * 如果传入，将使用该值作为指示器宽度，但仍会限制在 48-112px 之间
   * 如果不传入，则根据文本宽度自动计算
   */
  indicatorWidth: {
    type: Number,
    default: undefined,
  },
  /**
   * 自定义底部横条的颜色
   * 如果传入，将使用该值作为指示器背景色
   * 如果不传入，则使用默认主题色
   */
  indicatorColor: {
    type: String,
    default: undefined,
  },
};

/**
 * TabItem 组件的 props
 */
export const tabItemProps = {
  /**
   * 标签的唯一标识符
   */
  index: {
    type: [String, Number] as PropType<TabsIndex>,
    required: true,
  },
  /**
   * 标签的显示文本
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * 是否禁用该标签
   * @default false
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * 激活与未激活状态的图标
   */
  icons: {
    type: Object as PropType<TabItemIcons>,
    default: () => ({}),
  },
};

